import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../../common/breadcrumbs";
import { globalLoader, handleServerValidations } from "../../../../../utils/commonfunction";
import CustomError from "../../../../../utils/customError";
import { AddOffer, OfferDetails } from "../../../services/serviceOffer.services";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { Tooltip, Whisper } from "rsuite";
import Loader from "../../../common/loader";

const OfferManagementAdd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [previewImages, setPreviewImages] = useState({
    vedic_astrology: null,
    numerology: null,
    tarot_reading: null,
    palmistry: null,
    common: null,
  });
  const [defaultData, setDefaultData] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);

  const services = [
    "vedic_astrology",
    "numerology",
    "tarot_reading",
    "palmistry",
    "common",
  ];

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: t("Offer Management Add"), url: "" },
  ];

  useEffect(() => {
    OfferDetails()
      .then((response) => {
        const data = response?.data || {};
        setDefaultData(data);
        const images = {};
        services.forEach(service => {
          images[service] = data[service]?.banner_image || null;
        });
        setPreviewImages(images);
        setTimeout(() => setDataLoaded(true), 100);
      })
      .catch(error => {
        console.log("error=====>", error);
      });
  }, []);

  // Validation: Accept only 1 or 0
  const validationSchema = Yup.object(
    services.reduce((schema, service) => {
      schema[`${service}_discount`] = Yup.number()
        .typeError(t("Discount must be a number"))
        .min(0, t("Discount cannot be less than 0"))
        .max(100, t("Discount cannot exceed 100"))
        .required(t("Discount percentage is required"));
      schema[`${service}_banner_image`] = Yup.mixed()
        .nullable()
        .required(t("Banner image is required"));
      schema[`${service}_status`] = Yup.number()
        .oneOf([1, 0], t("Status must be 1 (active) or 0 (inactive)"))
        .required(t("Status is required"));
      return schema;
    }, {})
  );

  // Initial values: status defaults to 1 or existing, else 0
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: services.reduce((values, service) => {
      values[`${service}_discount`] = defaultData[service]?.discount_percentage ?? "";
      values[`${service}_banner_image`] = defaultData[service]?.banner_image ?? null;
      // Accept 1 or 0 or true/false from API, default to 1 (active)
      const rawStatus = defaultData[service]?.status;
      values[`${service}_status`] =
        rawStatus === undefined
          ? 1
          : (rawStatus === 1 || rawStatus === true ? 1 : 0);
      return values;
    }, {}),
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      setSubmitted(true);
      const formData = new FormData();
      services.forEach(service => {
        formData.append(`${service}_discount`, values[`${service}_discount`]);
        formData.append(`${service}_banner_image`, values[`${service}_banner_image`]);
        formData.append(`${service}_status`, values[`${service}_status`]); // 1 or 0
      });
      AddOffer(formData)
        .then(response => {
          setSubmitting(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            setSubmitted(false);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            setSubmitted(false);
          }
        })
        .catch(error => {
          console.log("error ====> ", error);
          globalLoader(false);
          setSubmitted(false);
        });
    },
  });

  const handleImageChange = (event, service) => {
    const file = event.target.files[0];
    if (file) {
      formik.setFieldValue(`${service}_banner_image`, file);
      setPreviewImages((prev) => ({
        ...prev,
        [service]: URL.createObjectURL(file),
      }));
    } else {
      formik.setFieldValue(`${service}_banner_image`, null);
      setPreviewImages((prev) => ({
        ...prev,
        [service]: null,
      }));
    }
  };

  console.log("formik values====>", formik.values);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded ? (
        <form onSubmit={formik.handleSubmit}>
          <div className="row row-sm">
            <div className="col-lg-12 col-md-12 animation_fade">
              <div className="card custom-card">
                <div className="card-body">
                  <h6 className="main-content-label mb-4">{t("Offer Management")}</h6>
                  {services.map((service) => (
                    <div key={service} className="mb-4 border-bottom pb-3">
                      <h5 className="mb-3 text-capitalize">{t(service.replace("_", " "))}</h5>
                      <div className="row">
                        <div className="col-md-6 form-group">
                          <label htmlFor={`${service}_discount`} className="text-left d-flex">
                            {t("Discount Percentage")} (%):
                            <span className="requirestar">*</span>
                          </label>
                          <input
                            id={`${service}_discount`}
                            name={`${service}_discount`}
                            type="number"
                            min="0"
                            max="100"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values[`${service}_discount`]}
                            className="form-control"
                            placeholder={t("Enter discount percentage")}
                          />
                          <span className="text-danger d-flex text-left">
                            <CustomError name={`${service}_discount`} form={formik} />
                          </span>
                        </div>

                        {/* Status dropdown, values 1 (active) and 0 (inactive) */}
                        <div className="col-md-6 form-group">
                          <label htmlFor={`${service}_status`} className="text-left d-flex">
                            {t("Status")}:
                            <span className="requirestar">*</span>
                          </label>
                          <select
                            id={`${service}_status`}
                            name={`${service}_status`}
                            className="form-control"
                            value={formik.values[`${service}_status`]}
                            onChange={e => formik.setFieldValue(`${service}_status`, Number(e.target.value))}
                            onBlur={formik.handleBlur}
                          >
                            <option value={1}>{t("Active")}</option>
                            <option value={0}>{t("Inactive")}</option>
                          </select>
                          <span className="text-danger d-flex text-left">
                            <CustomError name={`${service}_status`} form={formik} />
                          </span>
                        </div>

                        <div className="col-md-6 form-group">
                          <label htmlFor={`${service}_banner_image`} className="text-left d-flex">
                            {t("Banner Image")}:
                            <Whisper
                              placement="top"
                              controlId={`tooltip-${service}`}
                              trigger="hover"
                              speaker={<Tooltip>{t("image_support_tooltip")}</Tooltip>}
                            >
                              <span className="field-more-info mt-1 ms-1 cp"> ? </span>
                            </Whisper>
                            <span className="requirestar">*</span>
                          </label>
                          <input
                            id={`${service}_banner_image`}
                            name={`${service}_banner_image`}
                            accept="image/*"
                            type="file"
                            className="form-control imgInput"
                            onChange={(e) => handleImageChange(e, service)}
                          />
                          <span className="text-danger d-flex text-left">
                            <CustomError name={`${service}_banner_image`} form={formik} />
                          </span>
                          {previewImages[service] && (
                            <div className="mt-2">
                              <img
                                src={previewImages[service]}
                                alt={`${service}_banner`}
                                style={{ height: "100px" }}
                              />
                              <button
                                type="button"
                                className="btn btn-link text-danger p-0 ms-2"
                                onClick={() => {
                                  formik.setFieldValue(`${service}_banner_image`, null);
                                  setPreviewImages((prev) => ({
                                    ...prev,
                                    [service]: null,
                                  }));
                                  document.getElementById(`${service}_banner_image`).value = "";
                                }}
                              >
                                {t("Remove")}
                              </button>
                            </div>
                          )}
                        </div>

                      </div>
                    </div>
                  ))}
                  <div className="mt-4">
                    <button
                      className="btn btn-info me-2"
                      type="submit"
                      disabled={submitted}
                    >
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default OfferManagementAdd;
